import { useCallback } from 'react';

/**
 * All possible storage keys used throughout the application.
 */
type StorageKey =
  | 'access_info'
  | 'narrative-rating'
  | 'community_guidelines_accepted'
  // User's stored locale preference, used to track logged out users language preference.
  | 'userLocale'
  | 'coupon'
  // Session user ID, used in auth code login flow.
  | '_sws_suid'
  | 'screener-rules-persisted'
  | '__SWS_PLAID_LINK_TOKEN__'
  | 'supported-locale-detector-status'
  | 'has_dismissed_popular_screeners'
  // Session user plan type, used to track the last logged out users plan type.
  | '_sws_uwe74hff83fu';

async function getStorageObject() {
  if (RUNTIME_ENV === 'native') {
    const { Preferences } = await import('@capacitor/preferences');
    return {
      set(key: string, value: string) {
        Preferences.set({ key, value });
      },
      async get(key: string) {
        const result = await Preferences.get({ key });
        return result.value;
      },
      flush(key: string) {
        Preferences.remove({ key });
      },
    };
  } else {
    return {
      set(key: string, value: string) {
        window.localStorage.setItem(key, value);
      },
      async get(key: string) {
        return window.localStorage.getItem(key);
      },
      flush(key: string) {
        window.localStorage.removeItem(key);
      },
    };
  }
}
/**
 * Persistent storage wrapper for read/write operations.
 */
export function usePersistentStorage() {
  const set = useCallback(
    async (key: PrettifyLiteral<StorageKey>, value: string) => {
      try {
        const { set } = await getStorageObject();
        set(key, value);
      } catch {
        console.warn('Unable to set item in storage');
      }
    },
    []
  );
  const get = useCallback(async (key: PrettifyLiteral<StorageKey>) => {
    try {
      const { get } = await getStorageObject();
      return await get(key);
    } catch {
      return null;
    }
  }, []);

  const flush = useCallback(async (key: PrettifyLiteral<StorageKey>) => {
    try {
      const { flush } = await getStorageObject();
      flush(key);
    } catch {
      // silently fail
    }
  }, []);

  return { set, get, flush };
}
